import { FC, ReactElement } from 'react';
import { ICaremapNote } from '../notes/model/NotesClient';
import dayjs from 'dayjs';
import { Text } from '../components/Text';
import { createUseStyles } from 'react-jss';
import { IonAvatar, IonItem, isPlatform } from '@ionic/react';
import { useBreakpoints } from '../theme/useBreakpoints';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CareMapState } from '../caremap/CareMapState';
import { mediaMaxWidth } from '../theme/utils';
import { ReactionsRow } from './reactions/ReactionsRow';

const useStyles = createUseStyles({
    feedContainer: {
        marginTop: 32
    },

    itemWrapper: {
        cursor: 'pointer',
        marginBottom: 42,

        [mediaMaxWidth.sm]: {
            '& ion-item::part(native)': {
                paddingLeft: 0
            }
        }
    },

    item: {
        '--border-radius': '8px'
    },

    avatarRow: {
        display: 'flex',
        paddingTop: 12,

        '& ion-avatar': {
            width: 48,
            height: 48
        }
    },

    avatarInitials: {
        background: 'var(--ion-color-step-100)',
        borderRadius: '50%',
        textTransform: 'uppercase',
        display: 'flex',
        width: 48,
        height: 48,
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 25,
        fontWeight: 500,
        color: 'var(--ion-color-mediumtext)'
    },

    postInfo: {
        marginTop: 4,
        marginLeft: 8,
        display: 'flex',
        flexDirection: 'column'
    },

    roleOrSection: {
        textTransform: 'capitalize',
        color: 'var(--ion-color-step-400)'
    },

    content: {
        marginTop: 4,
        display: '-webkit-box',
        overflow: 'hidden !important',
        textOverflow: 'ellipsis',
        '-webkit-box-orient': 'vertical',
        '-webkit-line-clamp': 10,
        // TODO: not needed anymore?? majority of browsers support -webkit-line-clamp
        // https://caniuse.com/css-line-clamp
        // for non-webkit browsers (Firefox)
        //maxHeight: '11.2rem',
        // .sc-ion-label-md-s p sets a smaller font-size than we want
        fontSize: 'inherit !important'
    },

    smallText: {
        fontSize: 14
    }
});

// NOTE: currently "posts" are actually ICaremapNote(s)

export type ICommunityFeedListProps = {
    posts?: ICaremapNote[];
    editPostHandler: (post: ICaremapNote) => void;
};

export const CommunityFeedList: FC<ICommunityFeedListProps> = ({ posts, editPostHandler }) => {
    const classes = useStyles();
    const { isAboveBreakpoint } = useBreakpoints();

    const careMapState = useInstance(CareMapState);

    const getAvatar = (post: ICaremapNote) => {
        let avatar: ReactElement;
        const pictureUrl = post.createdByUser?.pictureUrl;

        if (pictureUrl) {
            avatar = (
                <IonAvatar>
                    <img src={pictureUrl} alt="user avatar" />
                </IonAvatar>
            );
        } else {
            avatar = (
                <div className={classes.avatarInitials}>
                    {post.createdByUser?.firstName?.substring(0,1)}
                    {post.createdByUser?.lastName?.substring(0,1)}
                </div>
            );
        }

        return avatar;
    };

    const getPostDate = (post: ICaremapNote) => {
        const now = dayjs();
        const postDate = dayjs(post.createdAt);
        const diff = now.diff(postDate, 'days');
        return diff < 7 ? dayjs().to(postDate) : postDate.format('dddd, MMMM Do');
    };

    const getPostTitle = (post: ICaremapNote) => {
        const noteTarget = post.otherAttributes.noteTarget;
        const {member} = careMapState.getSupportGroupMemberInformation(noteTarget.memberId);
        const roleOrSection = member ? member.role : noteTarget.section !== 'patient' ? noteTarget.section : '';

        return <>
            <>
                {post.title ? <>{post.title}&nbsp;&nbsp;</> : ''}
                <span className={classes.roleOrSection}>{roleOrSection}</span>
            </>
        </>;
    };

    // order by createdAt desc, filter out private posts
    posts = (posts || []).sort((n1, n2) => dayjs(n1.createdAt).isAfter(n2.createdAt) ? -1 : 1);
    posts = (posts || []).filter(post => !post.isPrivate);

    return (
        <div data-type="community-feed-list" className={classes.feedContainer}>
            {posts.map(post => (
                <div
                    key={post.noteId}
                    data-type="post-item"
                    data-node-id={post.noteId}
                    className={classes.itemWrapper}
                >
                    <IonItem
                        lines="none"
                        button
                        data-id={post.noteId}
                        className={classes.item}
                        onClick={() => editPostHandler(post)}
                        detail={!isPlatform('capacitor') && isAboveBreakpoint('sm')}
                    >
                        <div style={{width: '100%', paddingBottom: 8}}>
                            <div className={classes.avatarRow}>
                                {getAvatar(post)}
                                <div className={classes.postInfo}>
                                    <Text type="subtitle">
                                        <>{post.createdByUser?.firstName || 'Anonymous'}&nbsp;{post.createdByUser?.lastName}</>
                                    </Text>
                                    <div style={{marginTop: 5}} className={classes.smallText}>
                                        <Text type="detail">{getPostDate(post)}</Text>
                                    </div>
                                </div>
                            </div>
                            <div style={{marginTop: 12, marginLeft: 4}}>
                                <Text type="subtitle">
                                    {getPostTitle(post)}
                                </Text>
                                <p
                                    data-type="content"
                                    className={classes.content}
                                    dangerouslySetInnerHTML={{ __html: post.html.split('\n').join('<br/>')}}
                                />
                            </div>
                        </div>
                    </IonItem>
                    <ReactionsRow post={post} />
                </div>
            ))}
        </div>
    );
};
